import React, { useEffect, useState } from "react";
import { Box, Button, ButtonGroup } from "@mui/material";
import UnifiedWaveformPlayer, { PlayerType } from "./components/UnifiedWaveformPlayer";
import { Outlet, useLocation } from "react-router-dom";
import { SoundProvider } from "../../../context/juno/SoundContext";
import { useTranslation } from "react-i18next";
import { useCustomNavigate } from "../../../hooks/useCustomNavigate";
import { RowBox } from "../../../utils/styledBox";
import { GradientTypography } from "../../../utils/gradientTypography";
import { GraphicEq as GraphicEqIcon } from "@mui/icons-material";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { VoiceProvider } from "../../../context/VoiceContext";
import { UnifiedAudioProvider } from "../../../context/juno/UnifiedAudioContext";

const SoundGenerator: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useCustomNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    // パスによって初期タブを設定
    if (location.pathname.includes("voice-generator")) {
      return "voice";
    } else if (location.pathname.includes("music-generator")) {
      return "music";
    }
    // デフォルトは音声タブ
    return "voice";
  });

  // タブ変更ハンドラー
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "voice") {
      navigate("/juno/sounds/voice-generator");
    } else if (tab === "music") {
      navigate("/juno/sounds/music-generator");
    }
  };

  const [playerType, setPlayerType] = useState<PlayerType>("voice");

  // activeTabに基づいてplayerTypeを更新
  useEffect(() => {
    setPlayerType(activeTab === "voice" ? "voice" : "music");
  }, [activeTab]);

  useEffect(() => {
    // location.pathnameが変わったときにactiveTabを更新
    if (location.pathname.includes("voice-generator")) {
      setActiveTab("voice");
    } else if (location.pathname.includes("music-generator")) {
      setActiveTab("music");
    }
  }, [location.pathname]);

  return (
    <VoiceProvider>
      <SoundProvider>
        <UnifiedAudioProvider>
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
            <Box
              sx={{
                px: 2,
                mb: 1,
                mt: 1,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <RowBox sx={{ alignItems: "center" }}>
                <GraphicEqIcon sx={{ mr: 1, color: "white" }} />
                <GradientTypography variant={"h5"} component={"h1"}>
                  Sound Generator
                </GradientTypography>
              </RowBox>

              <ButtonGroup aria-label="sound generator selector" size="small">
                <Button
                  onClick={() => handleTabChange("music")}
                  color={"secondary"}
                  variant={activeTab === "music" ? "contained" : "outlined"}
                  sx={{ width: "150px" }}
                >
                  <MusicNoteIcon sx={{ mr: 0.5 }} fontSize="small" />
                  {t("juno.sound.menu.music")} & {t("juno.sound.menu.sfx")}
                </Button>
                <Button
                  onClick={() => handleTabChange("voice")}
                  color={"secondary"}
                  variant={activeTab === "voice" ? "contained" : "outlined"}
                  sx={{ width: "150px" }}
                >
                  <KeyboardVoiceIcon sx={{ mr: 0.5 }} fontSize="small" />
                  {t("juno.sound.menu.voice")}
                </Button>
              </ButtonGroup>
            </Box>

            {/* 子ルートを表示 */}
            <Outlet />

            {/* 固定表示の波形プレーヤー */}
            <Box sx={{ position: "fixed", bottom: 0, zIndex: 10, left: 0, right: 0, mt: 2 }}>
              <UnifiedWaveformPlayer playerType={playerType} />
            </Box>
          </Box>
        </UnifiedAudioProvider>
      </SoundProvider>
    </VoiceProvider>
  );
};

export default SoundGenerator;
